import React from 'react';
import { RootCauseAnalysis } from '../RootCauseAnalysis';
import { HighPriorityIncidents } from '../HighPriorityIncidents';
import { IncidentOriginAnalysis } from '../IncidentOriginAnalysis';
import { StatsCard } from '../StatsCard';
import { CheckCircle2, Loader, AlertTriangle, AlertCircle, Clock } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

interface IncidentsSectionProps {
  incidents: any[];
}

export function IncidentsSection({ incidents }: IncidentsSectionProps) {
  // Calculate ticket states
  const ticketStats = React.useMemo(() => {
    const closedTickets = incidents.filter(ticket => {
      const state = (ticket.state || ticket.status || "").toLowerCase();
      return state === 'closed' || state === 'closed complete';
    }).length;

    const inProgressTickets = incidents.filter(ticket => {
      const state = (ticket.state || ticket.status || "").toLowerCase();
      return state === 'in progress' || state === 'pending' || state === 'work in progress';
    }).length;

    const resolvedTickets = incidents.filter(ticket => {
      const state = (ticket.state || ticket.status || "").toLowerCase();
      return state === 'resolved';
    }).length;

    return {
      closedTickets,
      inProgressTickets,
      resolvedTickets
    };
  }, [incidents]);

  // Calculate incident summary statistics
  const summary = React.useMemo(() => {
    const total = incidents.length;
    const byPriority: Record<string, number> = incidents.reduce((acc, incident) => {
      const priority = incident.priority || 'Unknown';
      acc[priority] = (acc[priority] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    const byStatus: Record<string, number> = incidents.reduce((acc, incident) => {
      const status = incident.state || incident.status || 'Unknown';
      acc[status] = (acc[status] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    const avgResolutionTime = incidents.reduce((acc, incident) => {
      if (incident.duration) {
        return acc + incident.duration;
      }
      return acc;
    }, 0) / (incidents.filter(i => i.duration).length || 1);

    return {
      total,
      byPriority,
      byStatus,
      avgResolutionTime
    };
  }, [incidents]);

  // Generate preventive recommendations
  const recommendations = React.useMemo(() => {
    const patterns = new Map<string, { count: number; examples: string[] }>();
    
    incidents.forEach(incident => {
      const description = incident.short_description || incident.description || '';
      const resolution = incident.resolution_notes || '';
      
      // Identify common patterns
      if (description.toLowerCase().includes('timeout') || description.toLowerCase().includes('connection')) {
        const key = 'Connection Issues';
        if (!patterns.has(key)) {
          patterns.set(key, { count: 0, examples: [] });
        }
        patterns.get(key)!.count++;
        if (patterns.get(key)!.examples.length < 2) {
          patterns.get(key)!.examples.push(description);
        }
      }
      
      if (description.toLowerCase().includes('null') || description.toLowerCase().includes('undefined')) {
        const key = 'Data Validation Issues';
        if (!patterns.has(key)) {
          patterns.set(key, { count: 0, examples: [] });
        }
        patterns.get(key)!.count++;
        if (patterns.get(key)!.examples.length < 2) {
          patterns.get(key)!.examples.push(description);
        }
      }
      
      if (description.toLowerCase().includes('memory') || description.toLowerCase().includes('performance')) {
        const key = 'Performance Issues';
        if (!patterns.has(key)) {
          patterns.set(key, { count: 0, examples: [] });
        }
        patterns.get(key)!.count++;
        if (patterns.get(key)!.examples.length < 2) {
          patterns.get(key)!.examples.push(description);
        }
      }
    });

    return Array.from(patterns.entries())
      .sort((a, b) => b[1].count - a[1].count)
      .map(([category, data]) => ({
        category,
        count: data.count,
        examples: data.examples,
        recommendations: getRecommendations(category)
      }));
  }, [incidents]);

  function getRecommendations(category: string): string[] {
    switch (category) {
      case 'Connection Issues':
        return [
          'Implement retry mechanisms with exponential backoff',
          'Add circuit breakers for external service calls',
          'Monitor connection pool usage and implement proper cleanup'
        ];
      case 'Data Validation Issues':
        return [
          'Implement strict input validation at API boundaries',
          'Add data type checking and null handling in critical paths',
          'Create automated data quality checks'
        ];
      case 'Performance Issues':
        return [
          'Implement caching strategies for frequently accessed data',
          'Optimize database queries and add proper indexing',
          'Set up performance monitoring and alerting'
        ];
      default:
        return ['Review and analyze patterns for specific recommendations'];
    }
  }

  const getPriorityColor = (priority: string) => {
    if (priority.includes('1') || priority.includes('Critical')) return 'bg-red-500';
    if (priority.includes('2') || priority.includes('High')) return 'bg-orange-500';
    if (priority.includes('3') || priority.includes('Medium')) return 'bg-yellow-500';
    return 'bg-blue-500';
  };

  const getStatusColor = (status: string) => {
    if (status.toLowerCase().includes('closed') || status.toLowerCase().includes('resolved')) {
      return 'bg-green-500';
    }
    if (status.toLowerCase().includes('in progress')) {
      return 'bg-blue-500';
    }
    return 'bg-yellow-500';
  };

  return (
    <>
      <h2 className="text-2xl font-bold mb-6">Incident Details</h2>
      <p className="text-muted-foreground mb-6">
        Detailed view of all incidents with filtering options.
      </p>
      
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-6">
        <StatsCard 
          title="Closed Tickets" 
          value={ticketStats.closedTickets} 
          icon={<CheckCircle2 className="h-6 w-6" />} 
          className="min-w-[180px]"
        />
        <StatsCard 
          title="Tickets In Progress" 
          value={ticketStats.inProgressTickets} 
          icon={<Loader className="h-6 w-6" />} 
          className="min-w-[180px]"
        />
        <StatsCard 
          title="Resolved Tickets" 
          value={ticketStats.resolvedTickets} 
          icon={<CheckCircle2 className="h-6 w-6" />} 
          className="min-w-[180px]"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-1 gap-6 mb-6">
        {/* Incident Summary Card */}
        {/* <Card>
          <CardHeader>
            <CardTitle>Incident Summary</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <AlertCircle className="h-4 w-4 text-blue-500" />
                  <h3 className="font-semibold">Total Incidents</h3>
                </div>
                <p className="text-2xl font-bold">{summary.total}</p>
              </div>
              
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-blue-500" />
                  <h3 className="font-semibold">Avg Resolution Time</h3>
                </div>
                <p className="text-2xl font-bold">
                  {Math.round(summary.avgResolutionTime / 3600)}h
                </p>
              </div>
              
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-blue-500" />
                  <h3 className="font-semibold">Open Incidents</h3>
                </div>
                <p className="text-2xl font-bold">
                  {summary.byStatus['In Progress'] || 0}
                </p>
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="font-semibold">Priority Distribution</h3>
              <div className="flex flex-wrap gap-2">
                {Object.entries(summary.byPriority).map(([priority, count]) => (
                  <Badge key={priority} className={`${getPriorityColor(priority)} text-white`}>
                    {priority}: {count}
                  </Badge>
                ))}
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="font-semibold">Status Distribution</h3>
              <div className="flex flex-wrap gap-2">
                {Object.entries(summary.byStatus).map(([status, count]) => (
                  <Badge key={status} className={`${getStatusColor(status)} text-white`}>
                    {status}: {count}
                  </Badge>
                ))}
              </div>
            </div>
          </CardContent>
        </Card> */}

        {/* Preventive Actions Card */}
        {/* <Card>
          <CardHeader>
            <CardTitle>Preventive Action Recommendations</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {recommendations.map((rec, index) => (
              <div key={index} className="space-y-2">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-yellow-500" />
                  <h3 className="font-semibold">{rec.category} ({rec.count} incidents)</h3>
                </div>
                <div className="pl-6 space-y-2">
                  <p className="text-sm text-muted-foreground">Example incidents:</p>
                  <ul className="list-disc pl-4 text-sm text-muted-foreground">
                    {rec.examples.map((example, i) => (
                      <li key={i}>{example}</li>
                    ))}
                  </ul>
                  <p className="text-sm text-muted-foreground mt-2">Recommended actions:</p>
                  <ul className="list-disc pl-4">
                    {rec.recommendations.map((action, i) => (
                      <li key={i} className="text-sm flex items-center gap-2">
                        <CheckCircle2 className="h-4 w-4 text-green-500" />
                        {action}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </CardContent>
        </Card> */}
      </div>

      <div className="space-y-6">
        <RootCauseAnalysis incidents={incidents} />
        <IncidentOriginAnalysis incidents={incidents} />
        <HighPriorityIncidents incidents={incidents} showFullTable />
      </div>
    </>
  );
}
